import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminCarsApi } from "../../http/cars/api";
import { UpdateCar } from "../../http/cars/types";
import ModalDialog from "../ui/ModalDialog";

type ConcludeCarLeasingDialogProps = {
  carId: string;
  onClose: () => void;
};

const ConcludeCarLeasingDialog: FC<ConcludeCarLeasingDialogProps> = ({ carId, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const carsApi = useAdminCarsApi();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const carSwr = carsApi.getCar(carId, aborter.current.signal);
  const [car] = useDataInitializer(carSwr.data, carSwr.error);

  useEffect(() => {
    carSwr.trigger();
  }, [carId]);

  useEffect(() => {
    carSwr.error && dispatch(showErrorNotification(i18n, carSwr.error));
  }, [carSwr.error]);

  const onConfirm = async () => {
    if (!car) return;

    const body: UpdateCar = {
      vehicleIdentificationNumber: car.vehicleIdentificationNumber,
      notes: car.notes,
      manufacturerId: car.manufacturer.id,
      model: car.model,
      hp: car.hp!,
      displacement: car.displacement,
      co2Emissions: car.co2Emissions,
      fuelType: car.fuelType,
      category: car.category,
      isPrivateCar: car.isPrivateCar,
      status: "returned",
      plateNumber: car.currentPlateNumber,
      registrationDate: car.registrationDate,
      currentVersionNumber: car.versionNumber,
      purchaseDate: car.purchaseDate,
      soldDate: car.soldDate,
    };

    setIsSubmitting(true);
    try {
      await carsApi.updateCar(carId, body);
      dispatch(showSuccessNotification(i18n.t));
      onClose();
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      isSubmitting={isSubmitting}
      title={i18n.t.carLeasings.conclude}
      actions={[
        {
          title: i18n.t.common.cancel,
          type: "secondary",
          onClick: onClose,
        },
        {
          title: i18n.t.common.yes,
          disabled: !car,
          onClick: onConfirm,
        },
      ]}
    >
      {i18n.t.carLeasings.questions.conclude}
    </ModalDialog>
  );
};

export default ConcludeCarLeasingDialog;
